// src/services/marketIntelligence.js
// Serviço de Inteligência de Mercado

import { supabase } from '../config/supabase';

// URL base das funções Netlify
const FUNCTIONS_BASE = '/.netlify/functions';

/**
 * Executa coleta de notícias
 */
export const collectNews = async (queries = null) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE}/collect-news`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ queries }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao coletar notícias:', error);
    throw error;
  }
};

/**
 * Executa coleta de indicadores IBGE
 */
export const collectIBGE = async (indicators = null) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE}/collect-ibge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ indicators }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao coletar indicadores IBGE:', error);
    throw error;
  }
};

/**
 * Executa monitoramento de concorrentes
 */
export const collectCompetitors = async () => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE}/collect-competitors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao monitorar concorrentes:', error);
    throw error;
  }
};

/**
 * Executa coleta completa (todos os coletores)
 */
export const collectAll = async (collectors = ['news', 'ibge', 'competitors']) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE}/collect-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ collectors }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erro na coleta completa:', error);
    throw error;
  }
};

/**
 * Busca alertas não lidos
 */
export const getUnreadAlerts = async (limit = 10) => {
  const { data, error } = await supabase
    .from('market_alerts')
    .select('*')
    .eq('is_read', false)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
};

/**
 * Busca notícias recentes
 */
export const getRecentNews = async (limit = 20, filters = {}) => {
  let query = supabase
    .from('market_news')
    .select('*')
    .order('published_at', { ascending: false })
    .limit(limit);

  if (filters.sentiment) {
    query = query.eq('sentiment', filters.sentiment);
  }

  if (filters.isCompetitor !== undefined) {
    query = query.eq('is_competitor', filters.isCompetitor);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Busca tendências
 */
export const getTrends = async (keyword = null, days = 30) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  let query = supabase
    .from('market_trends')
    .select('*')
    .gte('date', startDate.toISOString().split('T')[0])
    .order('date', { ascending: true });

  if (keyword) {
    query = query.eq('keyword', keyword);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Busca indicadores econômicos
 */
export const getEconomicIndicators = async (indicatorCode = null, months = 12) => {
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - months);

  let query = supabase
    .from('economic_indicators')
    .select('*')
    .gte('period_date', startDate.toISOString().split('T')[0])
    .order('period_date', { ascending: false });

  if (indicatorCode) {
    query = query.eq('indicator_code', indicatorCode);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Busca atualizações de concorrentes
 */
export const getCompetitorUpdates = async (competitorId = null, limit = 20) => {
  let query = supabase
    .from('competitor_updates')
    .select('*, competitors(name, website)')
    .order('detected_at', { ascending: false })
    .limit(limit);

  if (competitorId) {
    query = query.eq('competitor_id', competitorId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Marca alerta como lido
 */
export const markAlertAsRead = async (alertId) => {
  const { error } = await supabase
    .from('market_alerts')
    .update({ is_read: true })
    .eq('id', alertId);

  if (error) throw error;
  return true;
};

/**
 * Marca alerta como acionado
 */
export const markAlertAsActioned = async (alertId, notes = '') => {
  const { error } = await supabase
    .from('market_alerts')
    .update({
      is_actioned: true,
      actioned_at: new Date().toISOString(),
      action_notes: notes
    })
    .eq('id', alertId);

  if (error) throw error;
  return true;
};

/**
 * Busca estatísticas gerais
 */
export const getMarketStats = async () => {
  const [
    { count: unreadAlerts },
    { count: totalNews },
    { count: totalTrends },
    { count: activeCompetitors }
  ] = await Promise.all([
    supabase
      .from('market_alerts')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false),
    supabase
      .from('market_news')
      .select('*', { count: 'exact', head: true }),
    supabase
      .from('market_trends')
      .select('*', { count: 'exact', head: true }),
    supabase
      .from('competitors')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true)
  ]);

  return {
    unreadAlerts: unreadAlerts || 0,
    totalNews: totalNews || 0,
    totalTrends: totalTrends || 0,
    activeCompetitors: activeCompetitors || 0
  };
};

/**
 * Busca configurações de monitoramento
 */
export const getMonitoringConfigs = async () => {
  const { data, error } = await supabase
    .from('monitoring_config')
    .select('*')
    .order('config_type', { ascending: true });

  if (error) throw error;
  return data;
};

/**
 * Adiciona configuração de monitoramento
 */
export const addMonitoringConfig = async (config) => {
  const { data, error } = await supabase
    .from('monitoring_config')
    .insert([{
      ...config,
      is_active: true
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Remove configuração de monitoramento
 */
export const deleteMonitoringConfig = async (configId) => {
  const { error } = await supabase
    .from('monitoring_config')
    .delete()
    .eq('id', configId);

  if (error) throw error;
  return true;
};

/**
 * Adiciona concorrente
 */
export const addCompetitor = async (competitor) => {
  const { data, error } = await supabase
    .from('competitors')
    .insert([competitor])
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Remove concorrente
 */
export const deleteCompetitor = async (competitorId) => {
  const { error } = await supabase
    .from('competitors')
    .delete()
    .eq('id', competitorId);

  if (error) throw error;
  return true;
};

export default {
  collectNews,
  collectIBGE,
  collectCompetitors,
  collectAll,
  getUnreadAlerts,
  getRecentNews,
  getTrends,
  getEconomicIndicators,
  getCompetitorUpdates,
  markAlertAsRead,
  markAlertAsActioned,
  getMarketStats,
  getMonitoringConfigs,
  addMonitoringConfig,
  deleteMonitoringConfig,
  addCompetitor,
  deleteCompetitor
};
